import { Geolocation } from "@capacitor/geolocation";
import type { Position, PositionOptions, CallbackID } from "@capacitor/geolocation";
import { GeolocateControl } from 'mapbox-gl';
import type { FitBoundsOptions, IControl, Map, Marker } from 'mapbox-gl';
import PermissionsService from "../common/PermissionsService";

/**
 * Opciones del control, con la forma que construye el constructor.
 */
interface GeolocateOptions {
    positionOptions: PositionOptions;
    fitBoundsOptions: FitBoundsOptions;
    trackUserLocation: boolean;
    showAccuracyCircle: boolean;
    showUserLocation: boolean;
    showUserHeading: boolean;
    geolocation?: typeof Geolocation;
}

/**
 * Miembros internos de GeolocateControl que este fork usa.
 *
 * @types/mapbox-gl solo publica el constructor y trigger(), pero esta clase es
 * una copia de las tripas del control original y toca una veintena de campos y
 * metodos privados. Se declaran aqui para poder tiparlos sin tocar el runtime.
 */
export interface GeolocateControlInternals extends IControl {
    options: GeolocateOptions;
    _setup: boolean;
    _container: HTMLElement;
    _map: Map | undefined;
    _watchState: string;
    _geolocateButton: HTMLButtonElement;
    _geolocationWatchID: CallbackID | undefined;
    _numberOfWatches: number;
    _noTimeout: boolean;
    _supportsGeolocation: boolean | undefined;
    _lastKnownPosition: Position | null;
    _timeoutId: ReturnType<typeof setTimeout> | undefined;
    _userLocationDotMarker: Marker | undefined;
    _accuracyCircleMarker: Marker | undefined;
    _updateMarkerRotationThrottled: () => unknown;

    _onSuccess(position: Position): void;
    _onError(error: unknown): void;
    _onZoom(): void;
    _finish(): void;
    _setupUI(): void;
    _updateCamera(position: Position): void;
    _updateMarker(position: Position | null): void;
    _updateMarkerRotation(): void;
    _onDeviceOrientation(event: DeviceOrientationEvent): void;
    _addDeviceOrientationListener(): void;
    fire(event: object): unknown;
    on(type: string, listener: (...args: never[]) => unknown): unknown;
    trigger(): Promise<boolean> | boolean;
    _clearWatch(): Promise<void> | void;
    /**
     * Solo lo anade GronzeGeolocateControl; el control de mapbox no lo tiene.
     * Por eso todos los llamantes comprueban antes que sea una funcion.
     */
    reset?(): void;
    /**
     * Solo lo anade GronzeGeolocateControl (ver ahi la propiedad homonima); el
     * control de mapbox no lo tiene.
     */
    startInBackground?: boolean;
}

/**
 * GeolocateControl re-tipado con su superficie real.
 *
 * No se extiende el tipo publicado porque chocarian dos cosas: los miembros
 * privados de arriba, y que aqui trigger() es asincrono mientras que el tipo
 * declara que devuelve boolean (MapService si hace await sobre el). El valor en
 * tiempo de ejecucion es el mismo constructor de mapbox.
 */
const GeolocateControlBase = GeolocateControl as unknown as {
    new (options?: unknown): GeolocateControlInternals;
};

/**
 * Given a destination object and optionally many source objects,
 * copy all properties from the source objects into the destination.
 * The last source object given overrides properties from previous
 * source objects.
 *
 * @param dest destination object
 * @param sources sources from which properties are pulled
 * @private
 */
function extend$1(dest: Record<string, unknown>, ...sources: Record<string, unknown>[]) {
    for (const src of sources) {
        for (const k in src) {
            dest[k] = src[k];
        }
    }
    return dest;
}
class _Event {
    type: string;

    constructor(type: string, data: Record<string, unknown> = {}) {
        extend$1(this as unknown as Record<string, unknown>, data);
        this.type = type;
    }
}

export class GronzeGeolocateControl extends GeolocateControlBase {
    warnOnceHistory: Record<string, boolean> = {};

    /**
     * Arranca la geolocalizacion directamente en BACKGROUND, sin pasar por
     * ACTIVE_LOCK: el watch se pone en marcha y el marcador se actualiza, pero
     * la camara no salta nunca al GPS. Lo consume el propio trigger().
     *
     * Se aplica ahi, y no reaccionando a la primera posicion, porque cualquier
     * gancho posterior depende de cuando llega esa posicion respecto al estado
     * del control, y esa ventana no es igual en Android que en iOS: en iOS la
     * primera posicion puede llegar mientras el estado es todavia OFF (el
     * clearWatch del reset() previo es asincrono y el sistema entrega un fix
     * cacheado nada mas volver a foreground), y para cuando el estado llega a
     * ACTIVE_LOCK ya es tarde. Fijandolo dentro de trigger(), de forma
     * sincrona y antes de arrancar el watch, no queda ninguna ventana.
     */
    startInBackground = false;

    defaultOptions$1: GeolocateOptions = {
        positionOptions: {
            enableHighAccuracy: true,
            maximumAge: 0,
            timeout: 6000    /* 6 sec */
        },
        fitBoundsOptions: { maxZoom: 15 },
        trackUserLocation: true,
        showAccuracyCircle: true,
        showUserLocation: true,
        showUserHeading: true,
    };

    constructor(options: Partial<GeolocateOptions> = {}) {
        super();
        const geolocation = Geolocation;
        this.options = {
            ...this.defaultOptions$1,
            ...options,
            // positionOptions se fusiona campo a campo: quien solo pide
            // enableHighAccuracy no debe quedarse sin el maximumAge ni el
            // timeout por defecto, que es lo que haria el spread de arriba al
            // reemplazar el objeto entero.
            positionOptions: {
                ...this.defaultOptions$1.positionOptions,
                ...options.positionOptions,
            },
            // Siempre el de Capacitor: es la razon de ser de este control.
            geolocation,
        };

        this.bindAll([
            '_onSuccess',
            '_onError',
            '_onZoom',
            '_finish',
            '_setupUI',
            '_updateCamera',
            '_updateMarker',
            '_updateMarkerRotation',
            '_onDeviceOrientation'
        ], this);

        this._updateMarkerRotationThrottled = this.throttle(this._updateMarkerRotation, 20);
        this._numberOfWatches = 0;
    }

    /**
     * Given a destination object and optionally many source objects,
     * copy all properties from the source objects into the destination.
     * The last source object given overrides properties from previous
     * source objects.
     *
     * @param dest destination object
     * @param sources sources from which properties are pulled
     * @private
     */
    extend(dest: Record<string, unknown>, src: Record<string, unknown>) {
        for (const i in src)
            dest[i] = src[i];
        return dest;
    }

    /**
     * Given an array of member function names as strings, replace all of them
     * with bound versions that will always refer to `context` as `this`. This
     * is useful for classes where otherwise event bindings would reassign
     * `this` to the evented object or some other value: this lets you ensure
     * the `this` value always.
     *
     * @param fns list of member function names
     * @param context the context value
     * @example
     * function MyClass() {
     *   bindAll(['ontimer'], this);
     *   this.name = 'Tom';
     * }
     * MyClass.prototype.ontimer = function() {
     *   alert(this.name);
     * };
     * var myClass = new MyClass();
     * setTimeout(myClass.ontimer, 100);
     * @private
     */
    bindAll(fns: string[], context: object) {
        const target = context as Record<string, (...args: unknown[]) => unknown>;
        fns.forEach(fn => {
            if (!target[fn]) {
                return;
            }
            target[fn] = target[fn].bind(target);
        });
    }

    /**
     * Throttle the given function to run at most every `period` milliseconds.
     * @private
     */
    throttle(fn: () => void, time: number) {
        let pending = false;
        let timerId: ReturnType<typeof setTimeout> | null = null;
        const later = () => {
            timerId = null;
            if (pending) {
                fn();
                timerId = setTimeout(later, time);
                pending = false;
            }
        };
        return () => {
            pending = true;
            if (!timerId) {
                later();
            }
            return timerId;
        };
    }

    /**
     * Print a warning message to the console and ensure duplicate warning messages
     * are not printed.
     *
     * @private
     */
    warnOnce(message: string): void {
        if (!this.warnOnceHistory[message]) {
            // console isn't defined in some WebWorkers, see #2558
            if (typeof console !== 'undefined')
                console.warn(message);
            this.warnOnceHistory[message] = true;
        }
    }

    _checkGeolocationSupport(callback: (supported: boolean) => void): void {
        const updateSupport = (supported = !!Geolocation) => {
            this._supportsGeolocation = supported;
            callback(supported);
        };
        if (this._supportsGeolocation !== undefined) {
            callback(this._supportsGeolocation);
        } else {
            updateSupport(!!Geolocation);
        }
        /*
         else if (ref_properties.window.navigator.permissions !== undefined) {
            // navigator.permissions has incomplete browser support http://caniuse.com/#feat=permissions-api
            // Test for the case where a browser disables Geolocation because of an insecure origin;
            // in some environments like iOS16 WebView, permissions reject queries but still support geolocation
            this.options.geolocation.
            ref_properties.window.navigator.permissions.query({ name: 'geolocation' }).then(p => updateSupport(p.state !== 'denied')).catch(() => updateSupport());


        } else {
            updateSupport();
        }
        */
    }

    onRemove(): void {
        // clear the geolocation watch if exists
        if (this._geolocationWatchID !== undefined) {
            Geolocation.clearWatch({ id: this._geolocationWatchID });
            this._geolocationWatchID = undefined;
        }
        // clear the markers from the map
        if (this.options.showUserLocation && this._userLocationDotMarker) {
            this._userLocationDotMarker.remove();
        }
        if (this.options.showAccuracyCircle && this._accuracyCircleMarker) {
            this._accuracyCircleMarker.remove();
        }
        this._container.remove();
        this._map!.off('zoom', this._onZoom);
        this._map = undefined;
        this._numberOfWatches = 0;
        this._noTimeout = false;
    }

    /**
     * Trigger a geolocation event.
     *
     * @example
     * // Initialize the geolocate control.
     * const geolocate = new mapboxgl.GeolocateControl({
     *     positionOptions: {
     *         enableHighAccuracy: true
     *     },
     *     trackUserLocation: true
     * });
     * // Add the control to the map.
     * map.addControl(geolocate);
     * map.on('load', () => {
     *     geolocate.trigger();
     * });
     * @returns Returns `false` if called before control was added to a map, otherwise returns `true`.
     */
    async trigger(): Promise<boolean> {
        console.log('[GronzeGeolocateControl] trigger called', this._watchState);
        if (!this._setup) {
            this.warnOnce('Geolocate control triggered before added to a map');
            return false;
        }
        if (this.options.trackUserLocation) {
            // update watchState and do any outgoing state cleanup
            switch (this._watchState) {
                case 'OFF':
                    // turn on the GeolocateControl
                    // verify and wait for permissions
                    if (!await PermissionsService.ensureGeoPermission()) {
                        return false;
                    }

                    // El estado intermedio es un destino valido de arranque:
                    // el switch de estado entrante de mas abajo le pone la
                    // clase del boton, y _onSuccess mantiene BACKGROUND y se
                    // salta _updateCamera, que solo corre en ACTIVE_LOCK.
                    if (this.startInBackground) {
                        this.startInBackground = false;
                        this._watchState = 'BACKGROUND';
                    } else {
                        this._watchState = 'WAITING_ACTIVE';
                    }
                    this.fire(new _Event('trackuserlocationstart'));
                    break;
                case 'WAITING_ACTIVE':
                case 'ACTIVE_LOCK':
                case 'ACTIVE_ERROR':
                case 'BACKGROUND_ERROR':
                    // turn off the Geolocate Control
                    this._numberOfWatches = Math.max(0, (this._numberOfWatches || 0) - 1);
                    this._noTimeout = false;
                    this._watchState = 'OFF';
                    this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-waiting');
                    this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-active');
                    this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-active-error');
                    this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-background');
                    this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-background-error');
                    this.fire(new _Event('trackuserlocationend'));
                    break;
                case 'BACKGROUND':
                    this._watchState = 'ACTIVE_LOCK';
                    this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-background');
                    // set camera to last known location
                    if (this._lastKnownPosition)
                        this._updateCamera(this._lastKnownPosition);
                    this.fire(new _Event('trackuserlocationstart'));
                    break;
                default:
                    break;
            }
            // incoming state setup
            switch (this._watchState) {
                case 'WAITING_ACTIVE':
                    this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-waiting');
                    this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-active');
                    break;
                case 'ACTIVE_LOCK':
                    this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-active');
                    break;
                case 'ACTIVE_ERROR':
                    this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-waiting');
                    this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-active-error');
                    break;
                case 'BACKGROUND':
                    this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-background');
                    break;
                case 'BACKGROUND_ERROR':
                    this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-waiting');
                    this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-background-error');
                    break;
                default:
                    break;
            }
            // manage geolocation.watchPosition / geolocation.clearWatch
            if (this._watchState === 'OFF' && this._geolocationWatchID !== undefined) {
                // clear watchPosition as we've changed to an OFF state
                await this._clearWatch();
            } else if (this._geolocationWatchID === undefined) {
                // enable watchPosition since watchState is not OFF and there is no watchPosition already running
                this._geolocateButton.classList.add('mapboxgl-ctrl-geolocate-waiting');
                this._geolocateButton.setAttribute('aria-pressed', 'true');
                this._numberOfWatches++;
                let positionOptions: PositionOptions;
                if (this._numberOfWatches > 1) {
                    positionOptions = {
                        maximumAge: 600000,
                        timeout: 0
                    };
                    this._noTimeout = true;
                } else {
                    positionOptions = this.options.positionOptions;
                    this._noTimeout = false;
                }
                try {
                    const promiseResponse = await Geolocation.watchPosition(positionOptions,
                        (position, error) => {
                            if (error) {
                                // Si hay error (p.ej. permiso denegado), no dejamos el botón "girando".
                                try {
                                    this._onError(error);
                                } finally {
                                    console.log('[GronzeGeolocateControl] watchPosition error, resetting control');
                                    this.reset();
                                }
                            } else {
                                // Capacitor tipa position como nullable, pero solo
                                // llega null cuando viene error, que es la otra rama.
                                this._onSuccess(position!);
                            }
                        }
                    );
                    console.log('[GronzeGeolocateControl] watchPosition started', promiseResponse);
                    this._geolocationWatchID = promiseResponse;
                } catch {
                    //this._onError(error);
                    console.log('[GronzeGeolocateControl] watchPosition error, resetting control');
                    this.reset();
                }

                if (this.options.showUserHeading) {
                    this._addDeviceOrientationListener();
                }
            }
        } else {
            try {
                const position = await Geolocation.getCurrentPosition();
                this._onSuccess(position);
            } catch (error) {
                this._onError(error);
            }

            // This timeout ensures that we still call finish() even if
            // the user declines to share their location in Firefox
            this._timeoutId = setTimeout(this._finish, 10000    /* 10sec */);
        }
        return true;
    }

    reset(): void {
        console.log('[GronzeGeolocateControl] button reset called');
        this._watchState = 'OFF';
        this.startInBackground = false;
        this._numberOfWatches = 0;
        this._noTimeout = false;

        if (this._geolocationWatchID !== undefined) {
            const id = this._geolocationWatchID;
            this._geolocationWatchID = undefined;
            // No bloquear el UI: pero limpiar el ID inmediatamente evita carreras al volver a pulsar.
            Geolocation.clearWatch({ id }).catch((error) => {
                console.warn('[GronzeGeolocateControl] clearWatch error on reset', error);
            });
        }
        // remove listeners
        window.removeEventListener('deviceorientation', this._onDeviceOrientation);
        window.removeEventListener('deviceorientationabsolute', this._onDeviceOrientation);

        const button = this._geolocateButton;
        if (button) {
            button.classList.remove(
                'mapboxgl-ctrl-geolocate-waiting',
                'mapboxgl-ctrl-geolocate-active',
                'mapboxgl-ctrl-geolocate-active-error',
                'mapboxgl-ctrl-geolocate-background',
                'mapboxgl-ctrl-geolocate-background-error'
            );
            button.setAttribute('aria-pressed', 'false');
        }

        if (this.options.showUserLocation) {
            this._updateMarker(null);
        }
    }

    async _clearWatch(): Promise<void> {
        if (this._geolocationWatchID === undefined) return;

        // Invalida el id antes de await para evitar que un segundo click “vea” un watch activo.
        const id = this._geolocationWatchID;
        this._geolocationWatchID = undefined;

        try {
            await Geolocation.clearWatch({ id });
        } catch (error) {
            console.warn('[GronzeGeolocateControl] clearWatch error', error);
        } finally {
            window.removeEventListener('deviceorientation', this._onDeviceOrientation);
            window.removeEventListener('deviceorientationabsolute', this._onDeviceOrientation);
            if (this._geolocateButton) {
                this._geolocateButton.classList.remove('mapboxgl-ctrl-geolocate-waiting');
                this._geolocateButton.setAttribute('aria-pressed', 'false');
            }
            if (this.options.showUserLocation) {
                this._updateMarker(null);
            }
        }
    }
}
